'use client';

import { AutomationDayToggleGroup } from '../../automation/AutomaticExecutionsConfiguration';
import {
  buildEndHourSelectItems,
  buildStartHourSelectItems,
  buildTimezoneSelectItems,
  getBrowserTimezone,
  resolveScheduleHour,
  resolveScheduleTimezoneSelection,
  type AutomationSchedule,
  formatScheduleTimeRangeLabel,
  isValidScheduleHourRange,
} from '../../automation/automationScheduleShared';
import { Select, createSelectListCollection } from '../../../components/select';
import { Switch } from '../../../components/switch';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../../components/typography';
import { Tooltip } from '../../../components/tooltip';
import { SettingsCard } from '../SettingsCard';
import { SettingsEditDialog } from '../SettingsEditDialog';
import { SettingsRow } from '../SettingsRow';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon, HelpCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

export type ExecutionsConfigurationCardChange = {
  smokeTestEnabled?: boolean;
  supportsParallelExecution?: boolean;
  limitExecutionTimeEnabled?: boolean;
  automationSchedule?: AutomationSchedule;
};
export type ExecutionsConfigurationCardProps = {
  smokeTestEnabled: boolean;
  supportsParallelExecution: boolean;
  limitExecutionTimeEnabled: boolean;
  automationSchedule: AutomationSchedule;
  disabled?: boolean;
  isUpdating?: boolean;
  onChange: (change: ExecutionsConfigurationCardChange) => void;
};
type ToggleRowProps = {
  label: string;
  tooltipTitle: string;
  checked: boolean;
  yesLabel: string;
  noLabel: string;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
};
const ToggleRow = ({
  label,
  tooltipTitle,
  checked,
  yesLabel,
  noLabel,
  disabled,
  onCheckedChange,
}: ToggleRowProps) => {
  return (
    <HStack w="full" justifyContent="space-between" minH="32px" gap={4}>
      <HStack gap={1} minW={0}>
        <BodyDefault color="fg.neutral.muted">{label}</BodyDefault>
        <Tooltip title={tooltipTitle} positioning={{ strategy: 'fixed' }}>
          <Box display="flex" color="fg.neutral.muted">
            <HelpCircleIcon size={16} />
          </Box>
        </Tooltip>
      </HStack>
      <HStack gap={2} flexShrink={0}>
        <Switch
          size="sm"
          checked={checked}
          disabled={disabled}
          onCheckedChange={(details) => onCheckedChange(details.checked)}
        />
        <BodyDefault color="fg.neutral.default">
          {checked ? yesLabel : noLabel}
        </BodyDefault>
      </HStack>
    </HStack>
  );
};

export const ExecutionsConfigurationCard = ({
  smokeTestEnabled,
  supportsParallelExecution,
  limitExecutionTimeEnabled,
  automationSchedule,
  disabled,
  isUpdating,
  onChange,
}: ExecutionsConfigurationCardProps) => {
  return (
    <SettingsCard>
      <ExecutionsConfigurationContent
        smokeTestEnabled={smokeTestEnabled}
        supportsParallelExecution={supportsParallelExecution}
        limitExecutionTimeEnabled={limitExecutionTimeEnabled}
        automationSchedule={automationSchedule}
        disabled={disabled}
        isUpdating={isUpdating}
        onChange={onChange}
      />
    </SettingsCard>
  );
};

export const ExecutionsConfigurationContent = ({
  smokeTestEnabled,
  supportsParallelExecution,
  limitExecutionTimeEnabled,
  automationSchedule,
  disabled,
  isUpdating,
  onChange,
}: ExecutionsConfigurationCardProps) => {
  const tExecutions = useTranslations('designSystem.automation.executions');
  const tSchedule = useTranslations('designSystem.automation.schedule');
  const tCommon = useTranslations('designSystem.common');
  const [isTimezoneDialogOpen, setIsTimezoneDialogOpen] = useState(false);
  const [isTimeRangeDialogOpen, setIsTimeRangeDialogOpen] = useState(false);
  const [timezoneDraft, setTimezoneDraft] = useState<string | null>(null);
  const [startHourDraft, setStartHourDraft] = useState<number | null>(null);
  const [endHourDraft, setEndHourDraft] = useState<number | null>(null);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const selectedTimezone = resolveScheduleTimezoneSelection({
    scheduleTimezone: automationSchedule.timezone,
    browserTimezone,
  });
  const timezoneItems = buildTimezoneSelectItems({
    browserTimezone,
    selectedTimezone,
  });
  const timezoneCollection = createSelectListCollection({
    items: timezoneItems,
  });
  const selectedTimezoneLabel =
    timezoneItems.find(
      (timezoneOption) => timezoneOption.value === selectedTimezone,
    )?.label ?? selectedTimezone;
  const safeStartHourDraft = resolveScheduleHour(
    startHourDraft,
    automationSchedule.startHour,
  );
  const safeEndHourDraft = resolveScheduleHour(
    endHourDraft,
    automationSchedule.endHour,
  );
  const selectedDays = automationSchedule.daysOfWeek;
  const startHourDraftItems = useMemo(() => {
    return buildStartHourSelectItems(safeEndHourDraft);
  }, [safeEndHourDraft]);
  const endHourDraftItems = useMemo(() => {
    return buildEndHourSelectItems(safeStartHourDraft);
  }, [safeStartHourDraft]);
  const startHourDraftCollection = useMemo(
    () => createSelectListCollection({ items: startHourDraftItems }),
    [startHourDraftItems],
  );
  const endHourDraftCollection = useMemo(
    () => createSelectListCollection({ items: endHourDraftItems }),
    [endHourDraftItems],
  );
  const handleOpenTimezoneDialog = () => {
    setTimezoneDraft(selectedTimezone);
    setIsTimezoneDialogOpen(true);
  };

  const handleOpenTimeRangeDialog = () => {
    setStartHourDraft(automationSchedule.startHour);
    setEndHourDraft(automationSchedule.endHour);
    setIsTimeRangeDialogOpen(true);
  };

  const handleDaysChange = (day: number) => {
    const isSelected = automationSchedule.daysOfWeek.includes(day);
    const newAutomationSchedule: AutomationSchedule = {
      ...automationSchedule,
      daysOfWeek: isSelected
        ? automationSchedule.daysOfWeek.filter((d) => d !== day)
        : [...automationSchedule.daysOfWeek, day],
    };

    onChange({ automationSchedule: newAutomationSchedule });
  };

  const timeRangeValue = formatScheduleTimeRangeLabel({
    startHour: automationSchedule.startHour,
    endHour: automationSchedule.endHour,
    toLabel: tSchedule('timeRangeTo'),
    nextDayLabel: tSchedule('timeRangeNextDay'),
  });

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      <VStack gap={3} alignItems="stretch">
        <ToggleRow
          label={tExecutions('runAutomated.label')}
          tooltipTitle={tExecutions('runAutomated.tooltip')}
          checked={smokeTestEnabled}
          yesLabel={tExecutions('yes')}
          noLabel={tExecutions('no')}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ smokeTestEnabled: checked })}
        />

        {smokeTestEnabled ? (
          <>
            <Box h="1px" bg="border.default" />
            <ToggleRow
              label={tExecutions('parallel.label')}
              tooltipTitle={tExecutions('parallel.tooltip')}
              checked={supportsParallelExecution}
              yesLabel={tExecutions('yes')}
              noLabel={tExecutions('no')}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onChange({ supportsParallelExecution: checked })
              }
            />
            <Box h="1px" bg="border.default" />
            <ToggleRow
              label={tExecutions('limitTime.label')}
              tooltipTitle={tExecutions('limitTime.tooltip')}
              checked={limitExecutionTimeEnabled}
              yesLabel={tExecutions('yes')}
              noLabel={tExecutions('no')}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onChange({ limitExecutionTimeEnabled: checked })
              }
            />
          </>
        ) : null}
      </VStack>

      {smokeTestEnabled && limitExecutionTimeEnabled ? (
        <VStack gap={3} alignItems="stretch">
          <BodyStrong>{tExecutions('timeLimitation.title')}</BodyStrong>
          <SettingsCard>
            <SettingsRow
              label={tExecutions('daysOfWeek.label')}
              tooltip={tExecutions('daysOfWeek.tooltip')}
              control={
                <AutomationDayToggleGroup
                  daysOfWeek={selectedDays}
                  disabled={disabled}
                  onToggleDay={handleDaysChange}
                />
              }
            />
            <SettingsRow
              label={tSchedule('timezone')}
              value={selectedTimezoneLabel}
              action={{
                label: tCommon('edit'),
                onClick: handleOpenTimezoneDialog,
                disabled: isUpdating || disabled,
              }}
              isReadOnly={disabled}
            />
            <SettingsRow
              label={tSchedule('timeRange')}
              value={timeRangeValue}
              action={{
                label: tCommon('edit'),
                onClick: handleOpenTimeRangeDialog,
                disabled: isUpdating || disabled,
              }}
              isReadOnly={disabled}
            />
          </SettingsCard>
        </VStack>
      ) : null}

      <SettingsEditDialog
        open={isTimezoneDialogOpen}
        title={tSchedule('timezone')}
        onOpenChange={(open) => {
          if (!open) {
            setTimezoneDraft(null);
          }

          setIsTimezoneDialogOpen(open);
        }}
        onSubmit={() => {
          if (timezoneDraft) {
            onChange({
              automationSchedule: {
                ...automationSchedule,
                timezone: timezoneDraft,
              },
            });
          }

          setIsTimezoneDialogOpen(false);
        }}
        isSubmitDisabled={timezoneDraft === selectedTimezone}
        submitText={tCommon('saveChanges')}
      >
        <Box w="full">
          <Select.Root
            collection={timezoneCollection}
            positioning={{ sameWidth: true }}
            value={[timezoneDraft ?? selectedTimezone]}
            onValueChange={(details) => {
              const selectedValue = details.value[0];

              if (selectedValue) {
                setTimezoneDraft(selectedValue);
              }
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={tSchedule('selectTimezone')} />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup maxH="240px" overflowY="auto">
                  {timezoneItems.map((timezoneOption) => (
                    <Select.Item
                      key={timezoneOption.value}
                      item={timezoneOption}
                    >
                      <Select.ItemText>{timezoneOption.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </SettingsEditDialog>

      <SettingsEditDialog
        open={isTimeRangeDialogOpen}
        title={tSchedule('timeRange')}
        onOpenChange={(open) => {
          if (!open) {
            setStartHourDraft(null);
            setEndHourDraft(null);
          }

          setIsTimeRangeDialogOpen(open);
        }}
        onSubmit={() => {
          onChange({
            automationSchedule: {
              ...automationSchedule,
              startHour: safeStartHourDraft,
              endHour: safeEndHourDraft,
            },
          });
          setIsTimeRangeDialogOpen(false);
        }}
        isSubmitDisabled={
          !isValidScheduleHourRange({
            startHour: safeStartHourDraft,
            endHour: safeEndHourDraft,
          }) ||
          (safeStartHourDraft === automationSchedule.startHour &&
            safeEndHourDraft === automationSchedule.endHour)
        }
        submitText={tCommon('saveChanges')}
      >
        <HStack gap={2} alignItems="center" w="full">
          <Box flex={1}>
            <Select.Root
              collection={startHourDraftCollection}
              positioning={{ sameWidth: true }}
              value={[String(safeStartHourDraft)]}
              onValueChange={(details) => {
                const nextStart = Number.parseInt(details.value[0] ?? '', 10);

                if (!Number.isNaN(nextStart)) {
                  setStartHourDraft(nextStart);
                }
              }}
              size="sm"
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                  <ArrowDown01Icon size={14} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup maxH="240px" overflowY="auto">
                    {startHourDraftItems.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Box>
          <MetaDefault color="fg.neutral.subtle">
            {tSchedule('timeRangeTo')}
          </MetaDefault>
          <Box flex={1}>
            <Select.Root
              collection={endHourDraftCollection}
              positioning={{ sameWidth: true }}
              value={[String(safeEndHourDraft)]}
              onValueChange={(details) => {
                const nextEnd = Number.parseInt(details.value[0] ?? '', 10);

                if (!Number.isNaN(nextEnd)) {
                  setEndHourDraft(nextEnd);
                }
              }}
              size="sm"
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                  <ArrowDown01Icon size={14} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup maxH="240px" overflowY="auto">
                    {endHourDraftItems.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Box>
        </HStack>
      </SettingsEditDialog>
    </VStack>
  );
};
